
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, UserRole } from '../types';
import { authService } from '../services/auth';
import { useAuth } from '../context/AuthContext';
import { logAuthStep } from '../services/auth_utils';
import { LABELS } from '../constants/labels';
import { Lock, User as UserIcon, Users, AlertTriangle, CheckCircle2, Mail, KeyRound, ArrowRight, Bug, Info, Sparkles } from 'lucide-react';
import { BrandLogo } from './ui/BrandLogo';
import { FormContainer, Input, Button, Label } from './ui/DesignSystem';
import { REQUESTED_GROUP_MAX } from '../api/_lib/requestedGroup';

interface AuthScreenProps {
    onLoginSuccess: (user: User) => void;
    initialMode?: 'login' | 'register';
}

export const AuthScreen = ({ onLoginSuccess, initialMode = 'login' }: AuthScreenProps) => {
  const navigate = useNavigate();
  const { terminalError, setManualUser } = useAuth(); 
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [isResetMode, setIsResetMode] = useState(false);
  const [isMagicLinkMode, setIsMagicLinkMode] = useState(false); // New State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailedError, setDetailedError] = useState<any>(null);
  const [successMsg, setSuccessMsg] = useState('');
  
  // Form State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [requestedGroup, setRequestedGroup] = useState('');
  const [role, setRole] = useState<UserRole>(UserRole.TEACHER);

  useEffect(() => {
      if (terminalError) {
          let msg = "Ett inloggningsfel uppstod.";
          if (terminalError === 'ACCOUNT_EXPIRED') msg = "Giltighetstiden för kontot har gått ut. Kontakta din ansvariga administratör.";
          if (terminalError === 'ACCOUNT_DEACTIVATED') msg = "Detta konto är inaktiverat. Kontakta support om du tror att detta är ett fel.";
          if (terminalError === 'ACCOUNT_PENDING') msg = "Ditt konto väntar på godkännande av en administratör.";
          setError(msg);
      }
  }, [terminalError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setDetailedError(null);
    setSuccessMsg('');
    setLoading(true);

    try {
      if (isResetMode) {
        if (!email) throw new Error("Ange e-postadress.");
        await authService.resetPasswordForEmail(email);
        setSuccessMsg("Om adressen finns registrerad har vi skickat en återställningslänk.");
      }
      else if (isMagicLinkMode) {
        if (!email) throw new Error("Ange e-postadress.");
        await authService.sendMagicLink(email);
        setSuccessMsg("Om adressen finns registrerad har vi skickat en inloggningslänk. Klicka på den för att logga in.");
      }
      else if (isLogin) {
        logAuthStep('UI', 'Submit', `User clicking login: ${email}`, 'info');
        
        // 1. Perform Login
        const user = await authService.signIn(email, password);
        
        if (user) {
            logAuthStep('UI', 'Success', 'User object received', 'success');
            setManualUser(user);
            onLoginSuccess(user);
        }
      } else {
        // REGISTER FLOW
        if (!name) throw new Error("Namn krävs");
        
        logAuthStep('UI', 'Register', `New user: ${email}`, 'info');
        
        const newUser = await authService.signUp(email, password, name, role, undefined, requestedGroup);
        
        if (newUser && newUser.status === 'ACTIVE') {
            setManualUser(newUser);
            onLoginSuccess(newUser);
        } else {
            setSuccessMsg("Konto skapat! Du kan nu logga in.");
            setIsLogin(true);
        }
      }
    } catch (err: any) {
      console.error("Auth submit error:", err);
      logAuthStep('UI', 'Error', err.message, 'error');
      
      let msg = err.message || "Ett fel uppstod. Kontrollera uppgifterna.";
      
      if (msg === "ACCOUNT_EXPIRED") {
          msg = "Giltighetstiden för kontot har gått ut. Kontakta din ansvariga.";
      } else if (msg === "ACCOUNT_DEACTIVATED") {
          msg = "Kontot är inaktiverat eller borttaget.";
      } else if (msg === "ACCOUNT_PENDING") {
          if (!isLogin) {
              setSuccessMsg("Konto skapat! Ditt konto väntar nu på godkännande av en administratör. Du får ett mail när det är klart.");
              setIsLogin(true);
              setLoading(false);
              return; 
          }
          msg = "Ditt konto har inte godkänts av admin än.";
      } else if (msg.includes("Invalid login credentials")) {
          msg = "Fel e-post eller lösenord.";
      } else if (msg.includes("failed to fetch") || msg.includes("Failed to fetch")) {
          msg = "Kunde inte nå servern. Kontrollera din internetanslutning.";
      }

      setError(msg);
      if (err.technical || err.details) {
          setDetailedError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const pageTitle = isResetMode ? 'Återställ lösenord' : isMagicLinkMode ? 'Logga in med länk' : isLogin ? 'Logga in' : `Registrera ${LABELS.role.teacher}`;

  return (
    <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 w-full border border-white/20 font-sans relative max-h-[85vh] overflow-y-auto custom-scrollbar flex flex-col">
      
      {/* Header */}
      <div className="text-center mb-6 shrink-0">
        <div className="flex justify-center mb-4 transform scale-100">
            <BrandLogo variant="color" layout="boxed" />
        </div>
        <h1 className="text-xs font-black text-stone-400 uppercase tracking-widest">
          {pageTitle}
        </h1>
      </div>

      {error && (
        <div className={`p-3 rounded-xl text-xs mb-4 border shrink-0 ${terminalError ? 'bg-amber-50 text-amber-800 border-amber-100' : 'bg-red-50 text-red-600 border-red-100'}`}>
          <div className="flex items-start gap-2">
              {terminalError ? <Info size={14} className="mt-0.5 flex-shrink-0" /> : <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />}
              <span className="font-bold">{error}</span>
          </div>
        </div>
      )}

      {successMsg && (
        <div className="bg-green-50 text-green-700 p-3 rounded-xl text-xs mb-4 flex items-start gap-2 border border-green-100 shrink-0 animate-fade-in">
          <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1 leading-snug font-bold">
              {successMsg}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex-1">
        <FormContainer className="space-y-4">
            {!isLogin && !isResetMode && !isMagicLinkMode && (
            <>
                <div>
                    <Label>Ditt Namn</Label>
                    <Input 
                        required
                        placeholder="Förnamn Efternamn"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        icon={<UserIcon size={16} />}
                        className="py-3"
                    />
                </div>

                {/* Frivilligt: admin matchar svaret mot rätt grupp vid godkännandet. */}
                <div>
                    <Label htmlFor="register-group" optional>Vilken {LABELS.group.singular.toLowerCase()} tillhör du?</Label>
                    <Input
                        id="register-group"
                        placeholder="T.ex. IFK Bankeryd F13"
                        value={requestedGroup}
                        maxLength={REQUESTED_GROUP_MAX}
                        onChange={e => setRequestedGroup(e.target.value)}
                        icon={<Users size={16} />}
                        className="py-3"
                    />
                </div>

                <div className="bg-blue-50 text-blue-800 text-[10px] p-3 rounded-xl border border-blue-100 font-medium w-full max-w-xs mx-auto">
                    <strong>Notera:</strong> Som {LABELS.role.teacher.toLowerCase()} måste ditt konto godkännas av en administratör innan du kan logga in.
                </div>
            </>
            )}

            <div>
                {/* Vid inloggning går det också att skriva säljkoden från inloggningslappen. */}
                <Label>{isLogin && !isResetMode && !isMagicLinkMode ? 'E-post eller säljkod' : 'E-post'}</Label>
                <Input
                    required
                    type={isLogin && !isResetMode && !isMagicLinkMode ? 'text' : 'email'}
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    placeholder={isLogin && !isResetMode && !isMagicLinkMode ? 'namn@skola.se eller säljkod' : 'namn@skola.se'}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    icon={<Mail size={16} />}
                    className="py-3"
                />
            </div>

            {!isResetMode && !isMagicLinkMode && (
                <div>
                    <Label>Lösenord</Label>
                    <Input 
                        required
                        type="password"
                        minLength={6}
                        placeholder="••••••••"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        icon={<KeyRound size={16} />}
                        className="py-3"
                    />
                    {isLogin && (
                        <div className="flex justify-between items-center mt-1.5 w-full max-w-xs mx-auto">
                            <button 
                                type="button" 
                                onClick={() => { setIsMagicLinkMode(true); setError(''); setSuccessMsg(''); }}
                                className="text-[10px] text-coffee-600 hover:text-coffee-800 font-bold flex items-center gap-1 transition-colors"
                            >
                                <Sparkles size={10} /> Logga in med länk
                            </button>
                            <button 
                                type="button" 
                                onClick={() => { setIsResetMode(true); setError(''); setSuccessMsg(''); }}
                                className="text-[10px] text-stone-400 hover:text-stone-600 hover:underline font-bold transition-colors"
                            >
                                Glömt lösenord?
                            </button>
                        </div>
                    )}
                </div>
            )}

            <div className="pt-2">
                <Button 
                    type="submit" 
                    isLoading={loading}
                    icon={!loading && <ArrowRight size={16} />}
                    className="py-3.5"
                >
                    {isResetMode ? 'Skicka återställningslänk' : isMagicLinkMode ? 'Skicka inloggningslänk' : isLogin ? 'Logga in' : 'Registrera konto'} 
                </Button>
            </div>
        </FormContainer>
      </form>

      <div className="mt-6 pt-4 border-t border-stone-100 text-center space-y-3 shrink-0">
          {(isResetMode || isMagicLinkMode) ? (
              <button 
                  onClick={() => { setIsResetMode(false); setIsMagicLinkMode(false); setIsLogin(true); setError(''); setSuccessMsg(''); }}
                  className="text-coffee-600 font-bold hover:underline text-xs"
              >
                  Tillbaka till vanlig inloggning
              </button>
          ) : (
              <div>
                  <p className="text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">
                      {isLogin ? `Ny ${LABELS.role.teacher.toLowerCase()}?` : 'Redan registrerad?'}
                  </p>
                  <Button 
                      variant="secondary"
                      className="py-3"
                      onClick={() => { setIsLogin(!isLogin); setError(''); setSuccessMsg(''); }}
                  >
                      {isLogin ? 'Skapa konto' : 'Logga in'}
                  </Button>
              </div>
          )}
          
          <div className="pt-2">
            <button 
                onClick={() => navigate('/')}
                className="text-[10px] text-stone-400 hover:text-coffee-600 transition-colors font-bold uppercase tracking-widest"
            >
                Till startsidan
            </button>
          </div>
      </div>
    </div>
  );
};
